<template>
	<div>
		<div v-if="!localData.loanModel">
			<v-alert type="warning" icon="mdi-shield-lock-outline" outlined>
				لطفا ابتدا طرح وام مورد نظر را انتخاب کنید.
			</v-alert>
		</div>
		<div v-else>
			<div class="d-flex justify-center">
				<value-stepper size="h2" color="secondary" v-model="localData.amount" :isPrice="true" :defaultManual="true" :step="1000000" :min="0" :max="max" />
			</div>
			<div>
				<div style="padding: 10px;border-bottom: 1px dashed #eee;display: flex;flex-wrap: wrap;">
					<span>دوره بازپرداخت:</span>
					<span class="mr-1" v-if="localData.loanModel.allowedInstallments && localData.loanModel.allowedInstallments.includes('[')">
						<v-item-group v-model="localPaymentPeriod" inline>
							<div class="d-flex flex-wrap">
								<v-item v-for="allowedIns in JSON.parse(localData.loanModel.allowedInstallments)" :key="allowedIns.value" v-if="!paymentPeriod || paymentPeriod == allowedIns.value" :value="Number(allowedIns.value)">
									<template v-slot="{ active, toggle }">
										<div :class="active && 'active'" class="payment-period-item ml-2 mb-2 d-flex" style="" @click="toggle">
											{{ allowedIns.value }} ماهه
											<v-scroll-y-reverse-transition>
												<v-icon color="white" class="mr-2" small v-if="active">
													mdi-check
												</v-icon>
											</v-scroll-y-reverse-transition>
										</div>
									</template>
								</v-item>
							</div>
						</v-item-group>
					</span>
					<value-stepper v-else-if="!localData.loanModel.allowedInstallments || !isNaN(localData.loanModel.allowedInstallments)" size="h6" append="ماهه" color="secondary" v-model="localPaymentPeriod" :max="localData.loanModel && localData.loanModel.allowedInstallments ? Number(localData.loanModel.allowedInstallments) : 0" :min="1" />
				</div>
				<div style="padding: 10px;border-bottom: 1px dashed #eee;" v-if="localData.loanModel.maxMonthGap !== 1 && !paymentPeriod">
					<span>فاصله بین هر قسط:</span>
					<span class="mr-1">
						<value-stepper size="h6" color="secondary" v-model="localData.monthGap" append="ماه" :max="localData.loanModel ? localData.loanModel.maxMonthGap : 10" :min="1" />
					</span>
				</div>
				<div style="padding: 10px;">
					<div class="d-flex justify-space-between">
						<div>
							<span>مبلغ هر قسط:</span><span class="secondary--text mr-1">{{ String(installment).toPriceFormat() }} تومان</span>
						</div>
						<div v-if="localData.installmentCount && localData.installmentCount != localPaymentPeriod">
							<small class="grey--text">({{ localData.installmentCount }} قسط)</small>
						</div>
					</div>

				</div>
				<div style="padding: 10px;border-bottom: 1px dashed #eee;" v-if="delay">
					<span>جریمه دیرکرد روزانه:</span><span class="secondary--text mr-1">{{ String(delay).toPriceFormat() }} تومان</span>
				</div>
				<div style="padding: 10px;border-bottom: 1px dashed #eee;" v-if="early">
					<span>سود زودکرد روزانه:</span><span class="secondary--text mr-1">{{ String(early).toPriceFormat() }} تومان</span>
				</div>
				<div style="display:flex; padding: 10px;border-bottom: 1px dashed #eee;" v-if="cost">
					<span>هزینه عملیات:</span><span class="secondary--text mr-1">{{ String(cost).toPriceFormat() }} تومان</span>
					<v-tooltip top>
						<template v-slot:activator="{ on, attrs }">
							<v-btn fab dark x-small color="secondary" outlined class="mr-2" style="transform:scale(0.7)">
								<v-icon dark v-bind="attrs" v-on="on">
									mdi-exclamation
								</v-icon>
							</v-btn>
						</template>
						<span>هزینه عملیات شامل تشکیل پرونده و بیمه تسهیلات بوده و از مبلغ تسهیلات دریافتی شما کسر خواهد شد.</span>
					</v-tooltip>
				</div>
				<div class="d-flex justify-space-between text-h4 pa-2 rounded-lg mb-2 mt-4" style="border-radius:10px" :style="{ 'border': `1px solid ${payableColor === 'secondary' ? 'purple' : payableColor}` }">
					<span :class="`${payableColor}--text`">{{ pureTitle }}:</span>
					<span :class="`${payableColor}--text`">{{ String(pure).toPriceFormat() }} تومان</span>
				</div>
				<div class="d-flex justify-space-between text-h4 pa-2 rounded-lg" :class="[payableColor]">
					<span class="white--text">کل بازپرداخت:</span>
					<span v-if="hasError" class="white--text">غیر مجاز</span>
					<span v-else class="white--text">{{ String(payable).toPriceFormat() }} تومان</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import _ from 'lodash'
	import { emptyId } from '@/util/emptyId'

	export default {
		name: 'LoanCalculator',
		props: {
			value: { type: Object },
			payableColor: { type: String, default: 'secondary' },
			pureTitle: { type: String, default: 'خالص دریافتی' },
			paymentPeriod: { type: Number, default: 0 },
			maximum: { type: Number, default: 0 }
		},
		components: {
			'value-stepper': () => import('@/components/ValueStepper'),
		},
		data: () => ({
			localData: { amount: 0 },
			localPaymentPeriod: 1,
			hasError: false,
		}),
		computed: {
			delay() {
				return this.installment ? Math.round(this.installment * this.localData.loanModel.delayPercent) : 0
			},
			early() {
				return this.installment ? Math.round(this.installment * this.localData.loanModel.earlyPercent) : 0
			},
			installment() {
				if (!this.localData.loanModel || !this.localData.amount || this.hasError)
					return 0
				let payable = 0, installment = 0

				if (this.localData.loanModel.loanProfitType === 0) {
					payable = ((this.localData.amount * (this.localData.installmentCount * this.localData.monthGap * this.localData.loanModel.profitPercent)) / 100 + Number(this.localData.amount))
					installment = this.localData.installmentCount ? Math.round(payable / this.localData.installmentCount) : 0
				}
				else {
					const principal = this.localData.amount
					const monthlyInterest = this.localData.loanModel?.profitPercent / 100 / 12
					installment = monthlyInterest
						? (principal * monthlyInterest) / (1 - Math.pow(1 + monthlyInterest, -this.localPaymentPeriod))
						: principal / this.localPaymentPeriod
					installment = Math.round(installment * this.localData.monthGap)
					//payable = Math.round(installment * this.localData.installmentCount)
				}

				this.localData.installment = installment
				return installment
			},
			payable() {
				if (!this.localData.loanModel || !this.localData.amount || this.hasError)
					return 0
				let payable = this.localData.installmentCount * this.installment
				return Math.round(payable)
			},
			cost() {
				if (!this.localData.loanModel || !this.localData.amount || !this.localData.loanModel.costsJson || this.hasError)
					return 0

				let costs = JSON.parse(this.localData.loanModel.costsJson)
				if (costs.some(x => x.month == this.localPaymentPeriod)) {
					costs = costs.filter(x => x.month == this.localPaymentPeriod)
					if (costs.some(x => x.gap == this.localData.monthGap)) {
						costs = costs.filter(x => x.gap == this.localData.monthGap)
					} else {
						costs = costs.filter(x => !x.gap)
					}
				}
				else
					costs = costs.filter(x => !x.month)
				const sum = costs.reduce((accumulator, object) => {
					return accumulator + Number(object.amount)
				}, 0)
				let cost = (this.localData.amount * sum) / 100
				return Math.round(cost)
			},
			pure() {
				if (!this.localData.loanModel || !this.localData.amount || this.hasError)
					return 0
				return this.localData.amount - this.cost
			},
			max() {
				let max = this.localData?.loanModel?.allowedInstallments?.includes('[') ? JSON.parse(this.localData?.loanModel?.allowedInstallments).find(x => x.value == this.localPaymentPeriod)?.max : 0
				max = this.maximum || max || this.localData.loanModel?.maxAmount || 0
				return max
			}
		},
		mounted() {
		},
		watch: {
			value: {
				immediate: true,
				deep: true,
				handler(val, oldVal) {
					if (val && (this.localData.loanModel?.id !== val.loanModel?.id || this.localData?.loanModel?.maxAmount != val.loanModel?.maxAmount)) {
						this.localData = val
						this.localPaymentPeriod = val.installmentCount * val.monthGap

						if (this.max && (this.localData.id === emptyId || !this.localData.id))
							this.localData.amount = this.max


						if (this.localData?.loanModel?.allowedInstallments?.includes('[')) {
							if (JSON.parse(this.localData.loanModel.allowedInstallments).findIndex(x => x.value === this.localPaymentPeriod) == -1) {
								this.localPaymentPeriod = Number(JSON.parse(this.localData.loanModel.allowedInstallments)[0].value)
							}
						} else if (this.localData?.loanModel?.allowedInstallments) {
							if (this.localData.installmentCount > Number(this.localData.loanModel.allowedInstallments))
								this.localData.installmentCount = Number(this.localData.loanModel.allowedInstallments)
						}

						if (this.localData?.loanModel?.monthGap) {
							if (this.localData.monthGap > Number(this.localData.loanModel.monthGap))
								this.localData.monthGap = Number(this.localData.loanModel.monthGap)
							else if (this.localData?.loanModel?.maxMonthGap === 1)
								this.localData.monthGap = 1
						}
					}
				}
			},
			localData: {
				deep: true,
				handler(val, oldVal) {
					this.$emit('input', _.cloneDeep(val))
				}
			},
			'localData.monthGap': {
				deep: true,
				handler(val, oldVal) {
					if (this.localPaymentPeriod % val === 0) {
						this.localData.installmentCount = this.localPaymentPeriod / val
						this.hasError = false
					} else {
						this.localData.installmentCount = 0
						this.hasError = true
					}
				}
			},
			localPaymentPeriod: {
				deep: true,
				immediate: true,
				handler(val, oldVal) {
					if (val % this.localData.monthGap === 0) {
						this.localData.installmentCount = val / this.localData.monthGap
						this.hasError = false
					} else {
						this.localData.installmentCount = 0
						this.hasError = true
					}
				}
			},
			paymentPeriod: {
				deep: true,
				immediate: true,
				handler(val, oldVal) {
					if (val)
						this.localPaymentPeriod = val
				}
			}
		},
		methods: {
			start(duration = 120) {
				var distance = duration
				var x = setInterval(() => {
					distance -= 1
					var minutes = Math.floor(distance / 60)
					var seconds = Math.floor(distance % 60)
					this.remain = minutes.toString().padStart(2, '0') + ":" + seconds.toString().padStart(2, '0')

					// If the count down is over, write some text
					if (distance < 0) {
						clearInterval(x)
						this.$emit('finish')
						this.remain = ''
					}
				}, 1000)
			}
		}
	}
</script>

<style scoped>
	.payment-period-item {
		border-radius: 30px;
		border: 1px solid purple;
		padding: 5px 6px;
		cursor: pointer;
		font-size: 12px;
	}

	.payment-period-item:hover {
		background: #ffe5ff;
	}

	.payment-period-item.active {
		background: purple;
		color: #fff;
	}
</style>